// src/layout/builders/ProjectInvite/ProjectInviteForm.jsx
import React, { useState } from "react";
import useProjectEnqStore from "../../../store/builders/useProjectEnqStore";
import SuccessPopup from "../../../components/common/SuccessPopup";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup"; // Import ConfirmationPopup
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom"; // Import useNavigate

const ProjectInviteForm = () => {
  const { sendProjectInvite } = useProjectEnqStore();
  const navigate = useNavigate(); // Initialize navigate
  const [customerDucktailId, setCustomerDucktailId] = useState("");
  const [projectData, setProjectData] = useState({
    projectName: "",
    projectLocation: "",
    projectBudget: "",
    startingDate: "",
    deadlineDate: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false); // Local state for API call loading

  // --- Success Popup State ---
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");
  // --- End Success Popup State ---

  // --- Confirmation Popup State ---
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [confirmPopupMessage, setConfirmPopupMessage] = useState("");
  // --- End Confirmation Popup State ---

  const handleChange = (e) => {
    setProjectData({ ...projectData, [e.target.id]: e.target.value });
  };

  const handleCustomerIdChange = (e) => {
    setCustomerDucktailId(e.target.value);
  };

  const clearForm = () => {
    setCustomerDucktailId("");
    setProjectData({
      projectName: "",
      projectLocation: "",
      projectBudget: "",
      startingDate: "",
      deadlineDate: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    // Basic validations
    if (!customerDucktailId) {
      toast.error("Please enter a Customer Ducktail ID.");
      return;
    }
    if (
      projectData.deadlineDate &&
      projectData.startingDate &&
      projectData.deadlineDate < projectData.startingDate
    ) {
      toast.error("Deadline must be after the project starting date.");
      return;
    }

    // --- Show Confirmation Popup ---
    setConfirmPopupMessage(`Are you sure you want to send a project invitation to Customer ID: ${customerDucktailId}?`);
    setIsConfirmPopupOpen(true);
    // --- Do not proceed with API call yet ---
  };

  // --- Function called when confirmation is given ---
  const handleConfirmInvite = async () => {
    setIsConfirmPopupOpen(false); // Close confirmation
    setIsSubmitting(true); // Start API loading state

    try {
      const result = await sendProjectInvite(customerDucktailId, projectData);

      if (result.success) {
        setSuccessPopupMessage(result.message);
        setIsSuccessPopupOpen(true); // Open success popup
        // Form cleared in closeSuccessPopupAndClear
      }
      // Error case is handled by toast within the store or caught below
    } catch (error) {
      console.error("Error sending project invite:", error);
      // toast.error("An unexpected error occurred."); // Generic fallback if needed
    } finally {
      setIsSubmitting(false); // Stop API loading state
    }
  };

  const closeSuccessPopupAndClear = () => {
    setIsSuccessPopupOpen(false);
    clearForm(); // Clear form after success popup is closed
    // Optionally navigate away or refresh the pending list tab if needed
    // navigate('/builder/projectinvite'); // Or trigger a refresh for the other tab
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="flex items-center justify-center p-3">
      {/* --- Render Success Popup --- */}
      <SuccessPopup
        isOpen={isSuccessPopupOpen}
        onClose={closeSuccessPopupAndClear}
        message={successPopupMessage}
      />

      {/* --- Render Confirmation Popup --- */}
      <ConfirmationPopup
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)} // Just close on cancel
        onConfirm={handleConfirmInvite} // Call handler to proceed
        message={confirmPopupMessage}
        title="Confirm Invitation"
        confirmText="Send Invite"
        isLoading={isSubmitting} // Use API loading state for confirm button
      />

      {/* The rest of the form remains the same */}
       <div className="w-full max-w-xl bg-white rounded-lg shadow-md p-5">
        <h2 className="text-lg font-semibold text-gray-700 mb-3 text-center">
          Project Invitation
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Customer Ducktail ID Input */}
          <div>
            <label
              htmlFor="customerId"
              className="block text-2xs font-medium text-gray-600 mb-1"
            >
              Ducktail ID *
            </label>
            <input
              type="text"
              id="customerId"
              value={customerDucktailId}
              onChange={handleCustomerIdChange}
              placeholder="Enter Customer's Ducktail ID"
              required
              disabled={isSubmitting || isConfirmPopupOpen} // Disable during confirm/submit
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          {/* Project Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Project Name */}
            <div>
              <label
                htmlFor="projectName"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Project Name *
              </label>
              <input
                type="text"
                id="projectName"
                value={projectData.projectName}
                onChange={handleChange}
                placeholder="Enter Project Name"
                required
                disabled={isSubmitting || isConfirmPopupOpen} // Disable during confirm/submit
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* Project Location */}
            <div>
              <label
                htmlFor="projectLocation"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Project Location *
              </label>
              <input
                type="text"
                id="projectLocation"
                value={projectData.projectLocation}
                onChange={handleChange}
                placeholder="Enter Project Location"
                required
                disabled={isSubmitting || isConfirmPopupOpen} // Disable during confirm/submit
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* Project Budget */}
            <div>
              <label
                htmlFor="projectBudget"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Project Budget (INR) *
              </label>
              <input
                type="text" // Use text to allow formatting, validation can be added
                id="projectBudget"
                value={projectData.projectBudget}
                onChange={handleChange}
                pattern="[0-9,]*" // Allow numbers and commas
                title="Please enter numbers and commas only (e.g., 10,00,000)"
                placeholder="Estimated Budget"
                required
                disabled={isSubmitting || isConfirmPopupOpen} // Disable during confirm/submit
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* Starting Date */}
            <div>
              <label
                htmlFor="startingDate"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Starting Date *
              </label>
              <input
                type="date"
                id="startingDate"
                value={projectData.startingDate}
                onChange={handleChange}
                min={today}
                required
                disabled={isSubmitting || isConfirmPopupOpen} // Disable during confirm/submit
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* Deadline Date */}
            <div>
              <label
                htmlFor="deadlineDate"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Deadline *
              </label>
              <input
                type="date"
                id="deadlineDate"
                value={projectData.deadlineDate}
                onChange={handleChange}
                min={projectData.startingDate || today} // Min date is start date or today
                required
                disabled={isSubmitting || isConfirmPopupOpen} // Disable during confirm/submit
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-2">
            <button
              type="button"
              onClick={clearForm}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-xs transition-colors"
              disabled={isSubmitting || isConfirmPopupOpen} // Disable during confirm/submit
            >
              Cancel
            </button>
            <button
              type="submit" // This now triggers the confirmation first
              className={`px-4 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs flex items-center justify-center transition-colors ${
                (isSubmitting || isConfirmPopupOpen) ? "opacity-70 cursor-not-allowed" : "" // Disable if confirming or submitting
              }`}
              disabled={isSubmitting || isConfirmPopupOpen} // Disable button itself
            >
              {/* Show loading only during API call, not during confirmation */}
              {isSubmitting ? (
                <>
                  <AiOutlineLoading3Quarters className="animate-spin mr-1.5 h-3 w-3" />
                  Sending...
                </>
              ) : (
                "Send Invite"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectInviteForm;